import React, { useEffect, useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogActions,
  Button,
  IconButton,
  Typography,
  Box,
  Chip,
  Grid,
  Card,
  CardContent,
  Avatar,
  Paper,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Tabs,
  Tab,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import TrendingUpIcon from "@mui/icons-material/TrendingUp";
import InventoryIcon from "@mui/icons-material/Inventory";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ScheduleIcon from "@mui/icons-material/Schedule";
import ShoppingCartIcon from "@mui/icons-material/ShoppingCart";
import TimelineIcon from "@mui/icons-material/Timeline";
import SettingsIcon from "@mui/icons-material/Settings";
import { styled, useTheme } from "@mui/material/styles";
import { inventoryService } from "../../utils/services/inventoryService";
import { formatDate } from "../../utils/formatDate";

const MetricCard = styled(Paper)(({ theme, color = "primary" }) => ({
  padding: theme.spacing(2),
  borderRadius: theme.spacing(1),
  border: `1px solid ${theme.palette.divider}`,
  background: `linear-gradient(135deg, ${theme.palette[color]?.main || theme.palette.primary.main}08, ${theme.palette[color]?.main || theme.palette.primary.main}04)`,
  borderLeft: `3px solid ${theme.palette[color]?.main || theme.palette.primary.main}`,
  transition: "all 0.2s ease-in-out",
  "&:hover": {
    transform: "translateY(-1px)",
    boxShadow: theme.shadows[2],
  },
}));

const ForecastDetailsModal = ({
  open,
  onClose,
  forecastData,
  onDiscard,
  client_id,
}) => {
  const theme = useTheme();
  const [activeTab, setActiveTab] = useState(0);
  const [forecastResults, setForecastResults] = useState([]);
  const [currentItem, setCurrentItem] = useState(null);

  useEffect(() => {
    if (!forecastData) {
      return;
    }
    let existing_skus = forecastResults.map((x) => x?.input?.sku);

    let res;
    if (Array.isArray(forecastData)) {
      res = [
        ...forecastResults,
        ...forecastData.filter((x) => !existing_skus.includes(x.input.sku)),
      ];
    } else if (
      forecastData?.input?.sku &&
      !existing_skus.includes(forecastData?.input?.sku)
    ) {
      res = [...forecastResults, forecastData];
    } else {
      res = forecastResults;
    }
    setForecastResults(res);
    setCurrentItem(res[0]);
  }, [forecastData]);

  useEffect(() => {
    setCurrentItem(forecastResults[activeTab]);
  }, [activeTab]);

  const onAccept = async () => {
    try {
      console.log(currentItem);
      await inventoryService.addForecastResult(client_id, currentItem);
      onIgnore();
    } catch (e) {
      console.error(e);
    }
  };

  const onIgnore = () => {
    if (forecastResults.length == 1) {
      onDiscard();
    } else {
      let new_result = forecastResults.filter((e, i) => i != activeTab);
      setForecastResults(new_result);
      setCurrentItem(new_result[activeTab]);
    }
  };

  const formatMetric = (value, label) => {
    if (value === null || value === undefined) return "N/A";
    if (typeof value === "number") {
      if (label?.toLowerCase().includes("date")) {
        return formatDate(value);
      }
      return isNaN(value)
        ? (value || "0").toLocaleString()
        : Math.round(Number(value));
    }
    return value;
  };

  const renderForecastCard = (item) => {
    if (!item || typeof item !== "object") {
      return (
        <Box sx={{ p: 4, textAlign: "center" }}>
          <Typography variant="body1" color="text.secondary">
            Invalid forecast data
          </Typography>
        </Box>
      );
    }

    return (
      <Card
        sx={{
          borderRadius: 3,
          border: `1px solid ${theme.palette.divider}`,
          boxShadow: 4,
          mb: 2,
        }}
      >
        <CardContent sx={{ p: 4 }}>
          {/* Key Metrics Grid */}
          {item.metrics && (
            <Box sx={{ mb: 4 }}>
              <Grid container spacing={2}>
                {/* ON HAND */}
                <Grid item xs={12} sm={6} md={3}>
                  <MetricCard color="primary">
                    <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                      <InventoryIcon
                        sx={{
                          fontSize: 20,
                          color: theme.palette.primary.main,
                        }}
                      />
                      <Box>
                        <Typography
                          variant="caption"
                          sx={{
                            fontFamily: "Poppins",
                            fontWeight: 600,
                            color: theme.palette.text.secondary,
                            fontSize: "11px",
                            textTransform: "uppercase",
                            letterSpacing: 0.5,
                            lineHeight: 1,
                          }}
                        >
                          On Hand
                        </Typography>
                        <Typography
                          variant="h6"
                          sx={{
                            fontFamily: "Poppins",
                            fontWeight: 700,
                            color: theme.palette.primary.main,
                            fontSize: "18px",
                            lineHeight: 1,
                          }}
                        >
                          {formatMetric(item.metrics.total_on_hand)}
                        </Typography>
                      </Box>
                    </Box>
                  </MetricCard>
                </Grid>

                {/* ORDERED / IN TRANSIT */}
                <Grid item xs={12} sm={6} md={3}>
                  <MetricCard color="success">
                    <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                      <TrendingUpIcon
                        sx={{
                          fontSize: 20,
                          color: theme.palette.success.main,
                        }}
                      />
                      <Box>
                        <Typography
                          variant="caption"
                          sx={{
                            fontFamily: "Poppins",
                            fontWeight: 600,
                            color: theme.palette.text.secondary,
                            fontSize: "11px",
                            textTransform: "uppercase",
                            letterSpacing: 0.5,
                            lineHeight: 1,
                          }}
                        >
                          Ordered
                        </Typography>
                        <Typography
                          variant="h6"
                          sx={{
                            fontFamily: "Poppins",
                            fontWeight: 700,
                            color: theme.palette.success.main,
                            fontSize: "18px",
                            lineHeight: 1,
                          }}
                        >
                          {formatMetric(item.metrics.total_in_transit)}
                        </Typography>
                      </Box>
                    </Box>
                  </MetricCard>
                </Grid>

                {/* REORDER POINT */}
                <Grid item xs={12} sm={6} md={3}>
                  <MetricCard color="error">
                    <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                      <Box>
                        <Typography
                          variant="caption"
                          sx={{
                            fontFamily: "Poppins",
                            fontWeight: 600,
                            color: theme.palette.text.secondary,
                            fontSize: "11px",
                            textTransform: "uppercase",
                            letterSpacing: 0.5,
                            lineHeight: 1,
                          }}
                        >
                          Reorder Point
                        </Typography>
                        <Typography
                          variant="h6"
                          sx={{
                            fontFamily: "Poppins",
                            fontWeight: 700,
                            color: theme.palette.error.main,
                            fontSize: "18px",
                            lineHeight: 1,
                          }}
                        >
                          {formatMetric(item.metrics.reorder_point)}
                        </Typography>
                      </Box>
                    </Box>
                  </MetricCard>
                </Grid>

                {/* SAFETY STOCK */}
                <Grid item xs={12} sm={6} md={3}>
                  <MetricCard color="warning">
                    <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                      <Box>
                        <Typography
                          variant="caption"
                          sx={{
                            fontFamily: "Poppins",
                            fontWeight: 600,
                            color: theme.palette.text.secondary,
                            fontSize: "11px",
                            textTransform: "uppercase",
                            letterSpacing: 0.5,
                            lineHeight: 1,
                          }}
                        >
                          Safety Stock
                        </Typography>
                        <Typography
                          variant="h6"
                          sx={{
                            fontFamily: "Poppins",
                            fontWeight: 700,
                            color: theme.palette.warning.main,
                            fontSize: "18px",
                            lineHeight: 1,
                          }}
                        >
                          {formatMetric(item.metrics.safety_stock_used)}
                        </Typography>
                      </Box>
                    </Box>
                  </MetricCard>
                </Grid>

                {/* AVG DAILY DEMAND */}
                <Grid item xs={12} sm={6} md={3}>
                  <MetricCard color="secondary">
                    <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                      <TrendingUpIcon
                        sx={{
                          fontSize: 20,
                          color: theme.palette.secondary.main,
                        }}
                      />
                      <Box>
                        <Typography
                          variant="caption"
                          sx={{
                            fontFamily: "Poppins",
                            fontWeight: 600,
                            color: theme.palette.text.secondary,
                            fontSize: "11px",
                            textTransform: "uppercase",
                            letterSpacing: 0.5,
                            lineHeight: 1,
                          }}
                        >
                          Avg Daily Demand
                        </Typography>
                        <Typography
                          variant="h6"
                          sx={{
                            fontFamily: "Poppins",
                            fontWeight: 700,
                            color: theme.palette.secondary.main,
                            fontSize: "18px",
                            lineHeight: 1,
                          }}
                        >
                          {formatMetric(item.metrics.avg_daily_demand)}
                        </Typography>
                      </Box>
                    </Box>
                  </MetricCard>
                </Grid>

                {/* LEAD TIME */}
                <Grid item xs={12} sm={6} md={3}>
                  <MetricCard color="error">
                    <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                      <ScheduleIcon
                        sx={{
                          fontSize: 20,
                          color: theme.palette.error.main,
                        }}
                      />
                      <Box>
                        <Typography
                          variant="caption"
                          sx={{
                            fontFamily: "Poppins",
                            fontWeight: 600,
                            color: theme.palette.text.secondary,
                            fontSize: "11px",
                            textTransform: "uppercase",
                            letterSpacing: 0.5,
                            lineHeight: 1,
                          }}
                        >
                          Lead Time
                        </Typography>
                        <Typography
                          variant="h6"
                          sx={{
                            fontFamily: "Poppins",
                            fontWeight: 700,
                            color: theme.palette.error.main,
                            fontSize: "18px",
                            lineHeight: 1,
                          }}
                        >
                          {formatMetric(item.metrics.lead_time_days)} days
                        </Typography>
                      </Box>
                    </Box>
                  </MetricCard>
                </Grid>

                {/* REC. ORDER QTY */}
                <Grid item xs={12} sm={6} md={3}>
                  <MetricCard color="warning">
                    <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                      <ShoppingCartIcon
                        sx={{
                          fontSize: 20,
                          color: theme.palette.warning.main,
                        }}
                      />
                      <Box>
                        <Typography
                          variant="caption"
                          sx={{
                            fontFamily: "Poppins",
                            fontWeight: 600,
                            color: theme.palette.text.secondary,
                            fontSize: "11px",
                            textTransform: "uppercase",
                            letterSpacing: 0.5,
                            lineHeight: 1,
                          }}
                        >
                          Recomended Reorder Quantity
                        </Typography>
                        <Typography
                          variant="h6"
                          sx={{
                            fontFamily: "Poppins",
                            fontWeight: 700,
                            color: theme.palette.warning.main,
                            fontSize: "18px",
                            lineHeight: 1,
                          }}
                        >
                          {formatMetric(item.metrics.recommended_order_qty)}
                        </Typography>
                      </Box>
                    </Box>
                  </MetricCard>
                </Grid>
                {item.metrics.reorder_by_date ? (
                  <Grid item xs={12} sm={6} md={3}>
                    <MetricCard color="success">
                      <Box
                        sx={{ display: "flex", alignItems: "center", gap: 2 }}
                      >
                        <ScheduleIcon
                          sx={{
                            fontSize: 20,
                            color: theme.palette.success.main,
                          }}
                        />
                        <Box>
                          <Typography
                            variant="caption"
                            sx={{
                              fontFamily: "Poppins",
                              fontWeight: 600,
                              color: theme.palette.text.secondary,
                              fontSize: "11px",
                              textTransform: "uppercase",
                              letterSpacing: 0.5,
                              lineHeight: 1,
                            }}
                          >
                            Next Reorder Date
                          </Typography>
                          <Typography
                            variant="h6"
                            sx={{
                              fontFamily: "Poppins",
                              fontWeight: 700,
                              color: theme.palette.warning.main,
                              fontSize: "18px",
                              lineHeight: 1,
                            }}
                          >
                            {new Date(
                              item.metrics.reorder_by_date,
                            )?.toLocaleDateString("en-US", {
                              month: "short",
                              day: "2-digit",
                              year: "numeric",
                            })}
                          </Typography>
                        </Box>
                      </Box>
                    </MetricCard>
                  </Grid>
                ) : (
                  <></>
                )}
              </Grid>
            </Box>
          )}

          {/* Forecast Parameters Accordion */}
          {item.input && (
            <Accordion
              defaultExpanded={true}
              sx={{
                boxShadow: "none",
                border: `1px solid ${theme.palette.divider}`,
                borderRadius: 2,
                mb: 2,
                "&:before": {
                  display: "none",
                },
                "&.Mui-expanded": {
                  margin: 0,
                  mb: 2,
                },
              }}
            >
              <AccordionSummary
                expandIcon={<ExpandMoreIcon />}
                sx={{
                  minHeight: 56,
                  "&.Mui-expanded": {
                    minHeight: 56,
                  },
                  "& .MuiAccordionSummary-content": {
                    margin: "16px 0",
                  },
                }}
              >
                <Typography
                  variant="h6"
                  sx={{
                    fontFamily: "Poppins",
                    fontWeight: 700,
                    color: theme.palette.text.primary,
                    display: "flex",
                    alignItems: "center",
                    gap: 1,
                  }}
                >
                  <SettingsIcon sx={{ fontSize: 20 }} />
                  Forecast Parameters
                </Typography>
              </AccordionSummary>
              <AccordionDetails sx={{ p: 3, pt: 0 }}>
                <Grid container spacing={2}>
                  <Grid item xs={12} sm={6} md={3}>
                    <Paper
                      sx={{
                        p: 2,
                        borderRadius: 2,
                        border: `1px solid ${theme.palette.divider}`,
                        bgcolor:
                          theme.palette.mode === "dark"
                            ? theme.palette.background.default
                            : theme.palette.grey[50],
                        minWidth: "200px",
                        flex: "1 1 auto",
                      }}
                    >
                      <Typography
                        variant="caption"
                        sx={{
                          fontFamily: "Poppins",
                          fontWeight: 600,
                          color: theme.palette.text.secondary,
                          fontSize: "10px",
                          textTransform: "uppercase",
                          letterSpacing: 0.5,
                          display: "block",
                          mb: 1,
                        }}
                      >
                        Forecast By
                      </Typography>
                      <Typography
                        variant="body2"
                        sx={{
                          fontFamily: "Poppins",
                          fontWeight: 600,
                          color: theme.palette.primary.main,
                          fontSize: "14px",
                        }}
                      >
                        {item.input.forecast_by || "sku"}
                      </Typography>
                    </Paper>
                  </Grid>
                  <Grid item xs={12} sm={6} md={3}>
                    <Paper
                      sx={{
                        p: 2,
                        borderRadius: 2,
                        border: `1px solid ${theme.palette.divider}`,
                        bgcolor:
                          theme.palette.mode === "dark"
                            ? theme.palette.background.default
                            : theme.palette.grey[50],
                        minWidth: "200px",
                        flex: "1 1 auto",
                      }}
                    >
                      <Typography
                        variant="caption"
                        sx={{
                          fontFamily: "Poppins",
                          fontWeight: 600,
                          color: theme.palette.text.secondary,
                          fontSize: "10px",
                          textTransform: "uppercase",
                          letterSpacing: 0.5,
                          display: "block",
                          mb: 1,
                        }}
                      >
                        Type
                      </Typography>
                      <Typography
                        variant="body2"
                        sx={{
                          fontFamily: "Poppins",
                          fontWeight: 600,
                          color: theme.palette.primary.main,
                          fontSize: "14px",
                        }}
                      >
                        {item.input.type || "Monthly"}
                      </Typography>
                    </Paper>
                  </Grid>
                  <Grid item xs={12} sm={6} md={3}>
                    <Paper
                      sx={{
                        p: 2,
                        borderRadius: 2,
                        border: `1px solid ${theme.palette.divider}`,
                        bgcolor:
                          theme.palette.mode === "dark"
                            ? theme.palette.background.default
                            : theme.palette.grey[50],
                        minWidth: "200px",
                        flex: "1 1 auto",
                      }}
                    >
                      <Typography
                        variant="caption"
                        sx={{
                          fontFamily: "Poppins",
                          fontWeight: 600,
                          color: theme.palette.text.secondary,
                          fontSize: "10px",
                          textTransform: "uppercase",
                          letterSpacing: 0.5,
                          display: "block",
                          mb: 1,
                        }}
                      >
                        Horizon
                      </Typography>
                      <Typography
                        variant="body2"
                        sx={{
                          fontFamily: "Poppins",
                          fontWeight: 600,
                          color: theme.palette.primary.main,
                          fontSize: "14px",
                        }}
                      >
                        {item.input.horizon} periods
                      </Typography>
                    </Paper>
                  </Grid>
                  {item.input.discount != null && (
                    <Grid item xs={12} sm={6} md={3}>
                      <Paper
                        sx={{
                          p: 2,
                          borderRadius: 2,
                          border: `1px solid ${theme.palette.divider}`,
                          bgcolor:
                            theme.palette.mode === "dark"
                              ? theme.palette.background.default
                              : theme.palette.grey[50],
                          minWidth: "200px",
                          flex: "1 1 auto",
                        }}
                      >
                        <Typography
                          variant="caption"
                          sx={{
                            fontFamily: "Poppins",
                            fontWeight: 600,
                            color: theme.palette.text.secondary,
                            fontSize: "10px",
                            textTransform: "uppercase",
                            letterSpacing: 0.5,
                            display: "block",
                            mb: 1,
                          }}
                        >
                          Discount Percentage
                        </Typography>
                        <Typography
                          variant="body2"
                          sx={{
                            fontFamily: "Poppins",
                            fontWeight: 600,
                            color: theme.palette.primary.main,
                            fontSize: "14px",
                          }}
                        >
                          {item.input.discount}%
                        </Typography>
                      </Paper>
                    </Grid>
                  )}
                  {item.input.forecast_by?.toLowerCase() === "product" ? (
                    <Grid item xs={12} sm={6} md={3}>
                      <Paper
                        sx={{
                          p: 2,
                          borderRadius: 2,
                          border: `1px solid ${theme.palette.divider}`,
                          bgcolor:
                            theme.palette.mode === "dark"
                              ? theme.palette.background.default
                              : theme.palette.grey[50],
                          minWidth: "200px",
                          flex: "1 1 auto",
                        }}
                      >
                        <Typography
                          variant="caption"
                          sx={{
                            fontFamily: "Poppins",
                            fontWeight: 600,
                            color: theme.palette.text.secondary,
                            fontSize: "10px",
                            textTransform: "uppercase",
                            letterSpacing: 0.5,
                            display: "block",
                            mb: 1,
                          }}
                        >
                          Product
                        </Typography>
                        <Typography
                          variant="body2"
                          sx={{
                            fontFamily: "Poppins",
                            fontWeight: 600,
                            color: theme.palette.primary.main,
                            fontSize: "14px",
                          }}
                        >
                          {item.input.product?.name || "N/A"}
                        </Typography>
                      </Paper>
                    </Grid>
                  ) : item.input.forecast_by?.toLowerCase() === "category" ? (
                    <Grid item xs={12} sm={6} md={3}>
                      <Paper
                        sx={{
                          p: 2,
                          borderRadius: 2,
                          border: `1px solid ${theme.palette.divider}`,
                          bgcolor:
                            theme.palette.mode === "dark"
                              ? theme.palette.background.default
                              : theme.palette.grey[50],
                          minWidth: "200px",
                          flex: "1 1 auto",
                        }}
                      >
                        <Typography
                          variant="caption"
                          sx={{
                            fontFamily: "Poppins",
                            fontWeight: 600,
                            color: theme.palette.text.secondary,
                            fontSize: "10px",
                            textTransform: "uppercase",
                            letterSpacing: 0.5,
                            display: "block",
                            mb: 1,
                          }}
                        >
                          Category
                        </Typography>
                        <Typography
                          variant="body2"
                          sx={{
                            fontFamily: "Poppins",
                            fontWeight: 600,
                            color: theme.palette.primary.main,
                            fontSize: "14px",
                          }}
                        >
                          {item.input.category ||
                            item.input.product_type ||
                            "N/A"}
                        </Typography>
                      </Paper>
                    </Grid>
                  ) : (
                    <Grid item xs={12} sm={6} md={3}>
                      <Paper
                        sx={{
                          p: 2,
                          borderRadius: 2,
                          border: `1px solid ${theme.palette.divider}`,
                          bgcolor:
                            theme.palette.mode === "dark"
                              ? theme.palette.background.default
                              : theme.palette.grey[50],
                          minWidth: "200px",
                          flex: "1 1 auto",
                        }}
                      >
                        <Typography
                          variant="caption"
                          sx={{
                            fontFamily: "Poppins",
                            fontWeight: 600,
                            color: theme.palette.text.secondary,
                            fontSize: "10px",
                            textTransform: "uppercase",
                            letterSpacing: 0.5,
                            display: "block",
                            mb: 1,
                          }}
                        >
                          SKU
                        </Typography>
                        <Typography
                          variant="body2"
                          sx={{
                            fontFamily: "Poppins",
                            fontWeight: 600,
                            color: theme.palette.primary.main,
                            fontSize: "14px",
                          }}
                        >
                          {item.input.sku || "N/A"}
                        </Typography>
                      </Paper>
                    </Grid>
                  )}
                </Grid>
              </AccordionDetails>
            </Accordion>
          )}

          {/* Forecast Data Points Accordion */}
          {item.forecast_adjusted && item.forecast_adjusted.length > 0 && (
            <Accordion
              defaultExpanded={false}
              sx={{
                boxShadow: "none",
                border: `1px solid ${theme.palette.divider}`,
                borderRadius: 2,
                mb: 2,
                "&:before": {
                  display: "none",
                },
                "&.Mui-expanded": {
                  margin: 0,
                  mb: 2,
                },
              }}
            >
              <AccordionSummary
                expandIcon={<ExpandMoreIcon />}
                sx={{
                  minHeight: 56,
                  "&.Mui-expanded": {
                    minHeight: 56,
                  },
                  "& .MuiAccordionSummary-content": {
                    margin: "16px 0",
                  },
                }}
              >
                <Typography
                  variant="h6"
                  sx={{
                    fontFamily: "Poppins",
                    fontWeight: 700,
                    color: theme.palette.text.primary,
                    display: "flex",
                    alignItems: "center",
                    gap: 1,
                  }}
                >
                  <TrendingUpIcon sx={{ fontSize: 20 }} />
                  Forecasted Inventory
                  <Chip
                    label={`${item.forecast_adjusted.length} periods`}
                    size="small"
                    variant="outlined"
                    sx={{
                      fontFamily: "Poppins",
                      fontSize: "11px",
                      ml: 1,
                    }}
                  />
                </Typography>
              </AccordionSummary>
              <AccordionDetails sx={{ p: 2, pt: 0 }}>
                <Box
                  sx={{
                    border: `1px solid ${theme.palette.divider}`,
                    borderRadius: 2,
                    p: 2,
                    bgcolor: theme.palette.background.paper,
                  }}
                >
                  <Grid container spacing={1}>
                    {item.forecast_adjusted.map((point, idx) => (
                      <Grid
                        item
                        xs={12}
                        sm={6}
                        md={Math.max(12 / item.forecast_adjusted.length, 2)}
                        key={idx}
                      >
                        <Paper
                          sx={{
                            p: 2,
                            borderRadius: 1,
                            border: `1px solid ${theme.palette.divider}`,
                            bgcolor:
                              theme.palette.mode === "dark"
                                ? theme.palette.background.default
                                : theme.palette.grey[50],
                            transition: "all 0.2s ease",
                            "&:hover": {
                              bgcolor: theme.palette.action.hover,
                              transform: "translateY(-1px)",
                            },
                          }}
                        >
                          <Box sx={{ textAlign: "center" }}>
                            <Typography
                              variant="caption"
                              sx={{
                                fontFamily: "Poppins",
                                fontWeight: 600,
                                fontSize: "13px",
                                color: theme.palette.text.primary,
                                mb: 0.5,
                                lineHeight: 1.2,
                              }}
                            >
                              {new Date(point.date)?.toLocaleDateString(
                                "en-US",
                                {
                                  timeZone: "UTC",
                                  month: "short",
                                  day: "2-digit",
                                  year: "numeric",
                                },
                              )}
                            </Typography>
                            <Typography
                              variant="h6"
                              sx={{
                                fontFamily: "Poppins",
                                fontWeight: 700,
                                fontSize: "18px",
                                color: theme.palette.primary.main,
                                lineHeight: 1.2,
                              }}
                            >
                              {formatMetric(point.predicted_qty) + " units"}
                            </Typography>
                          </Box>
                        </Paper>
                      </Grid>
                    ))}
                  </Grid>
                </Box>
              </AccordionDetails>
            </Accordion>
          )}

          {/* Inventory by Warehouse Accordion */}
          {item.forecast_adjusted && item.forecast_adjusted.length > 0 && (
            <Accordion
              defaultExpanded={false}
              sx={{
                boxShadow: "none",
                border: `1px solid ${theme.palette.divider}`,
                borderRadius: 2,
                "&:before": { display: "none" },
                "&.Mui-expanded": { margin: 0 },
              }}
            >
              <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                <Typography
                  variant="h6"
                  sx={{
                    fontFamily: "Poppins",
                    fontWeight: 700,
                    color: theme.palette.text.primary,
                    display: "flex",
                    alignItems: "center",
                    gap: 1,
                  }}
                >
                  <TimelineIcon sx={{ fontSize: 20 }} />
                  Inventory by Region
                </Typography>
              </AccordionSummary>
              <AccordionDetails sx={{ p: 3, pt: 0 }}>
                <Grid container spacing={2}>
                  <Grid item xs={12}>
                    <Box sx={{ overflowX: "auto", display: "flex" }}>
                      {[
                        {
                          warehouse: "West Coast",
                          percent: 28,
                        },
                        {
                          warehouse: "South Central",
                          percent: 18,
                        },
                        {
                          warehouse: "East Coast",
                          percent: 32,
                        },
                        {
                          warehouse: "Midwest",
                          percent: 22,
                        },
                      ].map((entry, idx) => (
                        <Grid
                          item
                          xs={12}
                          sm={6}
                          md={3}
                          key={entry.warehouse + idx}
                        >
                          <Paper
                            sx={{
                              p: 0.75,
                              borderRadius: 0.75,
                              border: `1px solid ${theme.palette.divider}`,
                              bgcolor:
                                theme.palette.mode === "dark"
                                  ? theme.palette.background.default
                                  : theme.palette.grey[50],
                              transition: "all 0.2s ease",
                              mr: 1,
                              "&:hover": {
                                bgcolor: theme.palette.action.hover,
                                transform: "translateY(-1px)",
                              },
                            }}
                          >
                            <Box sx={{ textAlign: "center" }}>
                              <Typography
                                variant="caption"
                                sx={{
                                  fontFamily: "Poppins",
                                  fontWeight: 600,
                                  fontSize: "14px",
                                  color: theme.palette.text.primary,
                                  mb: 0.125,
                                  lineHeight: 1.1,
                                }}
                              >
                                {entry.warehouse}
                              </Typography>
                              <Typography
                                variant="body2"
                                sx={{
                                  fontFamily: "Poppins",
                                  fontWeight: 700,
                                  fontSize: "18px",
                                  color: theme.palette.primary.main,
                                  lineHeight: 1.1,
                                }}
                              >
                                {formatMetric(
                                  (item.forecast_adjusted.reduce(
                                    (a, b) => a + Number(b.predicted_qty || 0),
                                    0,
                                  ) *
                                    entry.percent) /
                                    100,
                                )}
                              </Typography>
                            </Box>
                          </Paper>
                        </Grid>
                      ))}
                    </Box>
                  </Grid>
                </Grid>
              </AccordionDetails>
            </Accordion>
          )}
        </CardContent>
      </Card>
    );
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="xl"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 3,
          minHeight: "80vh",
          maxHeight: "90vh",
        },
      }}
    >
      {/* Enhanced Header */}
      <Box
        sx={{
          background: `linear-gradient(135deg, ${theme.palette.primary.main}08, ${theme.palette.primary.main}04)`,
          borderBottom: `1px solid ${theme.palette.divider}`,
          p: 3,
        }}
      >
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "flex-start",
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
            <Avatar
              sx={{
                bgcolor: theme.palette.primary.main,
                width: 48,
                height: 48,
              }}
            >
              <TrendingUpIcon sx={{ fontSize: 24 }} />
            </Avatar>
            <Box>
              <Typography
                variant="h5"
                sx={{
                  fontFamily: "Poppins",
                  fontWeight: 700,
                  color: theme.palette.text.primary,
                  mb: 0.5,
                }}
              >
                Forecast Results
              </Typography>
              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                <Typography
                  variant="body1"
                  sx={{
                    fontFamily: "Poppins",
                    fontWeight: 600,
                    color: theme.palette.primary.main,
                  }}
                >
                  SKU: {currentItem?.input?.sku}
                </Typography>
                <Chip
                  label="Latest Forecast"
                  size="small"
                  sx={{
                    fontFamily: "Poppins",
                    fontSize: "11px",
                    fontWeight: 500,
                  }}
                />
              </Box>
            </Box>
          </Box>
          <IconButton
            onClick={onClose}
            sx={{
              bgcolor: "var(--bg-primary)",
              "&:hover": {
                bgcolor: "var(--hover-bg)",
              },
            }}
          >
            <CloseIcon />
          </IconButton>
        </Box>
      </Box>

      <DialogContent
        sx={{
          p: 0,
          bgcolor:
            theme.palette.mode === "dark"
              ? theme.palette.background.default
              : theme.palette.grey[50],
        }}
      >
        {forecastResults.length > 1 ? (
          <Box>
            <Tabs
              value={activeTab}
              onChange={(_, newValue) => setActiveTab(newValue)}
              variant="scrollable"
              scrollButtons="auto"
              sx={{
                borderBottom: `1px solid ${theme.palette.divider}`,
                bgcolor: theme.palette.background.paper,
                px: 3,
                "& .MuiTab-root": {
                  fontFamily: "Poppins",
                  fontWeight: 600,
                  textTransform: "none",
                  minHeight: 48,
                },
              }}
            >
              {forecastResults.map((item, index) => (
                <Tab
                  key={index}
                  label={`SKU: ${item?.input?.sku || `Item ${index + 1}`}`}
                  sx={{ fontSize: "14px" }}
                />
              ))}
            </Tabs>
            <Box sx={{ p: 3 }}>{renderForecastCard(currentItem)}</Box>
          </Box>
        ) : (
          <Box sx={{ p: 3 }}>{renderForecastCard(currentItem)}</Box>
        )}
      </DialogContent>

      <DialogActions
        sx={{
          p: 3,
          borderTop: `1px solid ${theme.palette.divider}`,
          bgcolor:
            theme.palette.mode === "dark"
              ? theme.palette.background.default
              : theme.palette.grey[50],
        }}
      >
        <Button
          onClick={onAccept}
          variant="contained"
          color="primary"
          sx={{
            textTransform: "none",
            fontFamily: "Poppins",
            fontWeight: 600,
            px: 3,
            py: 1.5,
            borderRadius: 2,
            mr: 2,
          }}
        >
          Accept
        </Button>
        <Button
          onClick={onIgnore}
          variant="outlined"
          sx={{
            textTransform: "none",
            fontFamily: "Poppins",
            fontWeight: 600,
            px: 3,
            py: 1.5,
            borderRadius: 2,
          }}
        >
          Discard
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ForecastDetailsModal;
